(function () {
  const BERNIE_SELECTOR = "[data-bernie]";

  function qs(root, selector) {
    return root ? root.querySelector(selector) : null;
  }

  function qsa(root, selector) {
    return root ? Array.from(root.querySelectorAll(selector)) : [];
  }

  function escapeHtml(value) {
    return String(value == null ? "" : value)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;");
  }

  function readBernieFileAsDataUrl(file) {
    return new Promise(function (resolve, reject) {
      const reader = new FileReader();

      reader.onload = function () {
        resolve(reader.result);
      };

      reader.onerror = function () {
        reject(new Error("Could not read uploaded file."));
      };

      reader.readAsDataURL(file);
    });
  }

  function normaliseBernieAttachmentList(value) {
    if (!value) return [];
    if (Array.isArray(value)) return value.filter(Boolean);
    if (Array.isArray(value.attachments)) return value.attachments.filter(Boolean);
    return [value].filter(Boolean);
  }

  function validateBernieUploadFile(file) {
    const allowedTypes = [
      "image/jpeg",
      "image/png",
      "image/webp",
      "application/pdf"
    ];

    const isPdfByName = /\.pdf$/i.test(file.name || "");
    const isPdf = file.type === "application/pdf" || isPdfByName;

    if (!allowedTypes.includes(file.type) && !isPdfByName) {
      throw new Error("Please upload JPG, PNG, WebP or PDF files only.");
    }

    const maxBytes = isPdf ? 20 * 1024 * 1024 : 8 * 1024 * 1024;

    if (file.size > maxBytes) {
      throw new Error(
        isPdf
          ? "PDF is too large. Please upload each PDF under 20MB."
          : "Image is too large. Please upload each image under 8MB."
      );
    }
  }

  async function filesToBernieAttachments(files) {
    const selectedFiles = Array.from(files || []).filter(Boolean).slice(0, 5);

    if (!selectedFiles.length) return [];

    selectedFiles.forEach(validateBernieUploadFile);

    const attachments = [];

    for (const file of selectedFiles) {
      const isPdfByName = /\.pdf$/i.test(file.name || "");
      const dataUrl = await readBernieFileAsDataUrl(file);

      attachments.push({
        name: file.name || "uploaded-file",
        type: file.type || (isPdfByName ? "application/pdf" : ""),
        size: file.size || 0,
        dataUrl
      });
    }

    return attachments;
  }

  async function getBernieLocalAttachments(instance, options) {
    const input = qs(instance, ".bpc-bernie-upload-input");
    const attachments = await filesToBernieAttachments(input?.files || []);

    if (attachments.length && options?.consume && input) {
      input.value = "";
      input.dispatchEvent(new Event("change", { bubbles: true }));
    }

    return attachments;
  }

  async function getBernieLocalAttachment(instance, options) {
    const attachments = await getBernieLocalAttachments(instance, options);
    return attachments[0] || null;
  }

  function formatBernieMessage(text) {
    let safe = escapeHtml(text || "");

    safe = safe.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");

    const lines = safe.split(/\n/);
    let html = "";
    let inList = false;

    lines.forEach(function (line) {
      const trimmed = line.trim();
      const bulletMatch = trimmed.match(/^[-•]\s+(.+)/);

      if (bulletMatch) {
        if (!inList) {
          html += "<ul>";
          inList = true;
        }

        html += "<li>" + bulletMatch[1] + "</li>";
        return;
      }

      if (inList) {
        html += "</ul>";
        inList = false;
      }

      if (!trimmed) {
        html += "<br>";
      } else {
        html += "<p>" + trimmed + "</p>";
      }
    });

    if (inList) {
      html += "</ul>";
    }

    return html;
  }

  function getPageSnapshot(instance) {
    const context = String(instance.dataset.bernieContext || "").trim();

    if (typeof window.BPC_BERNIE_GET_SNAPSHOT === "function") {
      try {
        return window.BPC_BERNIE_GET_SNAPSHOT(context, instance) || {};
      } catch (err) {
        console.error("Bernie snapshot error:", err);
      }
    }

    return {
      page: context || "unknown",
      quoteMeta: {
        pageUrl: window.location.href
      }
    };
  }

  function getPageContext(instance) {
    const context = String(instance.dataset.bernieContext || "").trim();
    const source = String(instance.dataset.bernieSource || "").trim();
    const assistant = String(instance.dataset.bernieAssistant || "bernie").trim();

    if (typeof window.BPC_BERNIE_GET_CONTEXT === "function") {
      try {
        return window.BPC_BERNIE_GET_CONTEXT(context, instance) || {};
      } catch (err) {
        console.error("Bernie context error:", err);
      }
    }

    const isResultPage = context === "quote-thank-you";
    const isTechnical = assistant === "technical" || context === "technical-support";

    if (isTechnical) {
      return {
        assistantName: "Technical Bernie",
        pageType: "technical_support",
        source,
        tone: "friendly technical support helper",
        purpose: "Help customers with BPC ventilation product support, safe troubleshooting, part identification, filters, controls, ducting, airflow and installation questions.",
        currentUrl: window.location.href,
        pageTitle: document.title
      };
    }

    return {
      assistantName: "Bernie",
      pageType: isResultPage ? "instant_quote_result" : "instant_quote_form",
      source,
      tone: "friendly quote helper",
      purpose: isResultPage
        ? "Help the customer understand this saved MVHR/MEV instant quote, the calculation results and the generated kit lines."
        : "Help the customer complete the MVHR/MEV instant quote form, understand room inputs, options, airflow and quote results.",
      currentUrl: window.location.href,
      pageTitle: document.title
    };
  }

  async function getPageAttachments(instance, options) {
    const context = String(instance.dataset.bernieContext || "").trim();

    if (typeof window.BPC_BERNIE_GET_ATTACHMENTS === "function") {
      try {
        const externalAttachments = await window.BPC_BERNIE_GET_ATTACHMENTS(context, instance, options || {});
        const list = normaliseBernieAttachmentList(externalAttachments);

        if (list.length) return list;
      } catch (err) {
        console.error("Bernie attachment error:", err);
        throw err;
      }
    }

    if (typeof window.BPC_BERNIE_GET_ATTACHMENT === "function") {
      try {
        const externalAttachment = await window.BPC_BERNIE_GET_ATTACHMENT(context, instance, options || {});
        const list = normaliseBernieAttachmentList(externalAttachment);

        if (list.length) return list;
      } catch (err) {
        console.error("Bernie attachment error:", err);
        throw err;
      }
    }

    return await getBernieLocalAttachments(instance, options);
  }

  async function getPageAttachment(instance, options) {
    const attachments = await getPageAttachments(instance, options);
    return attachments[0] || null;
  }

  function getAvatarHtml(instance) {
    const avatarImg = qs(instance, ".bpc-bernie-avatar-img");

    if (avatarImg && avatarImg.getAttribute("src")) {
      return '<img src="' + escapeHtml(avatarImg.getAttribute("src")) + '" alt="">';
    }

    return "<span>🦦</span>";
  }

  function addMessage(instance, role, text) {
    const messages = qs(instance, ".bpc-bernie-messages");
    if (!messages) return null;

    const row = document.createElement("div");
    row.className =
      role === "user"
        ? "bpc-bernie-msg-row bpc-bernie-msg-row--user"
        : "bpc-bernie-msg-row bpc-bernie-msg-row--bot";

    if (role !== "user") {
      const avatar = document.createElement("div");
      avatar.className = "bpc-bernie-mini-avatar";
      avatar.innerHTML = getAvatarHtml(instance);
      row.appendChild(avatar);
    }

    const div = document.createElement("div");
    div.className =
      role === "user"
        ? "bpc-bernie-msg bpc-bernie-msg--user"
        : "bpc-bernie-msg bpc-bernie-msg--bot";

    if (role === "user") {
      div.textContent = String(text || "");
    } else {
      div.innerHTML = formatBernieMessage(text);
    }

    row.appendChild(div);
    messages.appendChild(row);
    messages.scrollTop = messages.scrollHeight;

    return row;
  }

  function addTyping(instance) {
    const messages = qs(instance, ".bpc-bernie-messages");
    if (!messages) return null;

    removeTyping(instance);

    const row = document.createElement("div");
    row.className = "bpc-bernie-msg-row bpc-bernie-msg-row--bot";
    row.setAttribute("data-bernie-typing", "1");

    const avatar = document.createElement("div");
    avatar.className = "bpc-bernie-mini-avatar";
    avatar.innerHTML = getAvatarHtml(instance);

    const typing = document.createElement("div");
    typing.className = "bpc-bernie-typing";
    typing.setAttribute("aria-label", "Bernie is typing");
    typing.innerHTML = "<span></span><span></span><span></span>";

    row.appendChild(avatar);
    row.appendChild(typing);

    messages.appendChild(row);
    messages.scrollTop = messages.scrollHeight;

    return row;
  }

  function removeTyping(instance) {
    const typing = qs(instance, "[data-bernie-typing='1']");
    if (typing) typing.remove();
  }

  function getTranscript(instance) {
    const history = instance.__bernieHistory || [];
    return history.slice(-30);
  }

  function getLatestQuestion(instance) {
    const history = getTranscript(instance);
    const latest = history.slice().reverse().find(function (item) {
      return item.role === "user";
    });

    return latest ? latest.content : "";
  }

  function getLatestAnswer(instance) {
    const history = getTranscript(instance);
    const latest = history.slice().reverse().find(function (item) {
      return item.role === "assistant";
    });

    return latest ? latest.content : "";
  }

  async function sendFeedback(instance, feedback) {
    const endpoint = instance.dataset.bernieEndpoint || "/apps/commercial-ai";
    const assistant = instance.dataset.bernieAssistant || "bernie";
    const snapshot = getPageSnapshot(instance);
    const context = getPageContext(instance);

    const payload = {
      action: "send_ai_feedback",
      assistant,
      feedbackType: feedback.rating === "up" ? "thumbs_up" : "thumbs_down",
      feedbackLabel: feedback.rating === "up" ? "Helpful" : "Not helpful",
      latestQuestion: getLatestQuestion(instance),
      latestAnswer: feedback.answer || getLatestAnswer(instance),
      extraNote: feedback.extraNote || "",
      transcript: getTranscript(instance),
      snapshot,
      context: {
        ...context,
        pageUrl: window.location.href,
        pageTitle: document.title,
        bernieSource: instance.dataset.bernieSource || "",
        bernieContext: instance.dataset.bernieContext || "",
        bernieAssistant: assistant
      },
      pageUrl: window.location.href,
      userAgent: navigator.userAgent
    };

    const res = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json"
      },
      body: JSON.stringify(payload)
    });

    const data = await res.json().catch(function () {
      return null;
    });

    if (!res.ok || !data || data.ok === false) {
      throw new Error((data && data.error) || "Could not send feedback.");
    }

    return data;
  }

  function addFeedbackControls(instance, answerText) {
    const messages = qs(instance, ".bpc-bernie-messages");
    if (!messages) return;

    const wrap = document.createElement("div");
    wrap.className = "bpc-bernie-feedback";
    wrap.innerHTML = ''
      + '<button type="button" data-bernie-feedback="up" aria-label="Good answer">👍</button>'
      + '<button type="button" data-bernie-feedback="down" aria-label="Bad answer">👎</button>';

    const note = document.createElement("div");
    note.className = "bpc-bernie-feedback-note";
    note.hidden = true;

    qsa(wrap, "button").forEach(function (btn) {
      btn.addEventListener("click", async function () {
        const rating = btn.getAttribute("data-bernie-feedback");

        qsa(wrap, "button").forEach(function (x) {
          x.classList.remove("is-selected");
          x.disabled = true;
        });

        btn.classList.add("is-selected");
        note.hidden = false;
        note.textContent = "Sending feedback…";

        try {
          await sendFeedback(instance, {
            rating,
            answer: answerText
          });

          note.textContent = "Thanks — feedback sent.";
        } catch (err) {
          console.error(err);
          note.textContent = "Could not send feedback.";
        }
      });
    });

    messages.appendChild(wrap);
    messages.appendChild(note);
    messages.scrollTop = messages.scrollHeight;
  }

  function removeExistingSupportPanel(instance) {
    qsa(instance, ".bpc-bernie-support-panel").forEach(function (el) {
      el.remove();
    });
  }

  function getPrefillCustomer(instance) {
    const snapshot = getPageSnapshot(instance) || {};
    const context = getPageContext(instance) || {};

    return {
      name:
        snapshot.customerName ||
        context.customerName ||
        context.name ||
        "",
      email:
        snapshot.customerEmail ||
        context.customerEmail ||
        context.email ||
        "",
      phone:
        snapshot.customerPhone ||
        context.customerPhone ||
        context.phone ||
        "",
      orderNumber:
        snapshot.orderNumber ||
        context.orderNumber ||
        snapshot.quoteRef ||
        ""
    };
  }

 async function createTechnicalTranscriptLink(instance, escalation) {
  const endpoint = instance.dataset.bernieEndpoint || "/apps/commercial-ai";
  const assistant = instance.dataset.bernieAssistant || "technical";
  const snapshot = getPageSnapshot(instance);
  const context = getPageContext(instance);

  const payload = {
    action: "create_technical_transcript",
    assistant,
    latestQuestion: getLatestQuestion(instance),
    latestAnswer: getLatestAnswer(instance),
    transcript: getTranscript(instance).map(function (item) {
      return {
        role: item.role || "",
        content: item.content || ""
      };
    }),
    snapshot: {
      page: snapshot.page || "",
      productOrSku: snapshot.productOrSku || "",
      manualProductMake: snapshot.manualProductMake || "",
      manualProductModel: snapshot.manualProductModel || "",
      selectedProduct: snapshot.selectedProduct || null,
      issueType: snapshot.issueType || "",
      powerStatus: snapshot.powerStatus || "",
      alreadyChecked: snapshot.alreadyChecked || "",
      issueDetails: snapshot.issueDetails || "",
      hasUpload: Boolean(snapshot.hasUpload),
      uploadCount: snapshot.uploadCount || 0,
      uploadNames: snapshot.uploadNames || "",
      quoteMeta: snapshot.quoteMeta || {}
    },
    context: {
      assistantName: context.assistantName || "",
      pageType: context.pageType || "",
      source: context.source || "",
      currentUrl: window.location.href,
      pageTitle: document.title,
      selectedProduct: context.selectedProduct || null,
      manualProduct: context.manualProduct || null,
      bernieSource: instance.dataset.bernieSource || "",
      bernieContext: instance.dataset.bernieContext || "",
      bernieAssistant: assistant
    },
    selectedProduct:
      snapshot?.selectedProduct ||
      context?.selectedProduct ||
      null,
    pageUrl: window.location.href,
    userAgent: navigator.userAgent,
    escalationReason: escalation?.reason || "",

    /* IMPORTANT:
       Only send file names/metadata to transcript storage.
       Do NOT send base64/dataUrl files here. */
    attachments: String(snapshot.uploadNames || "")
      .split(",")
      .map(function (name) {
        return name.trim();
      })
      .filter(Boolean)
      .slice(0, 5)
      .map(function (name) {
        return {
          name,
          type: "",
          size: 0
        };
      })
  };

  console.log("Creating Technical Bernie transcript:", payload);

  const res = await fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Accept": "application/json"
    },
    body: JSON.stringify(payload)
  });

  const raw = await res.text();

  console.log("Transcript create HTTP status:", res.status);
  console.log("Transcript create raw response:", raw);

  let data = null;

  try {
    data = raw ? JSON.parse(raw) : null;
  } catch (err) {
    throw new Error(
      "Transcript endpoint did not return JSON. Status " +
      res.status +
      ". Response starts: " +
      raw.slice(0, 160)
    );
  }

  if (!res.ok || !data || data.ok === false) {
    throw new Error(
      data?.error ||
      data?.details ||
      "Transcript endpoint returned an error. Status " + res.status
    );
  }

  if (!data.transcriptUrl && !data.url) {
    throw new Error("Transcript was created but no URL was returned.");
  }

  return data;
}


function addSupportEscalationPanel(instance, escalation) {
  const card = qs(instance, ".bpc-bernie-card");
  if (!card) return;

  removeExistingSupportPanel(instance);

  const panel = document.createElement("div");
  panel.className = "bpc-bernie-support-panel";

  panel.innerHTML = `
    <div class="bpc-bernie-support-head">
      <strong>AI couldn’t fully answer your question?</strong>
      <span>
        Request technical support and we’ll send the details, product context and conversation to the BPC technical team.
      </span>
    </div>

    ${
      escalation?.reason
        ? `<p class="bpc-bernie-support-reason">${escapeHtml(escalation.reason)}</p>`
        : ""
    }

    <div class="bpc-bernie-support-actions">
      <button type="button" class="bpc-bernie-support-send">Request technical support</button>
      <button type="button" class="bpc-bernie-support-cancel">Not now</button>
    </div>

    <div class="bpc-bernie-support-status" hidden></div>
  `;

  card.appendChild(panel);

  const openBtn = qs(panel, ".bpc-bernie-support-send");
  const cancelBtn = qs(panel, ".bpc-bernie-support-cancel");
  const status = qs(panel, ".bpc-bernie-support-status");

  cancelBtn?.addEventListener("click", function () {
    panel.remove();

    addMessage(
      instance,
      "bot",
      "No problem — I won’t open the support form yet. If you want technical support later, just reply **yes** or **request support**."
    );
  });

  openBtn?.addEventListener("click", async function () {
  const status = qs(panel, ".bpc-bernie-support-status");

  if (typeof window.BPC_OPEN_TECHNICAL_ZOHO_FORM !== "function") {
    status.hidden = false;
    status.classList.add("is-error");
    status.textContent = "Technical support form opener was not found on this page.";
    return;
  }

  const snapshot = getPageSnapshot(instance);
  const context = getPageContext(instance);
  const customer = getPrefillCustomer(instance);

  status.hidden = false;
  status.classList.remove("is-error");
  status.textContent = "Creating transcript link…";
  openBtn.disabled = true;

  let transcriptData = null;

  try {
    transcriptData = await createTechnicalTranscriptLink(instance, escalation);

    console.log("Technical Bernie transcript created:", transcriptData);
  } catch (err) {
    console.error("Technical Bernie transcript creation failed:", err);

    status.classList.add("is-error");
    status.textContent =
      "Could not create the transcript link: " + (err?.message || "Unknown error");

    openBtn.disabled = false;
    return;
  }

  const transcriptUrl = transcriptData?.transcriptUrl || transcriptData?.url || "";
const transcriptRef = transcriptData?.ref || transcriptData?.transcriptId || "";

  if (!transcriptUrl) {
    status.classList.add("is-error");
    status.textContent = "Transcript was created but no transcript URL was returned.";
    openBtn.disabled = false;
    return;
  }

  status.textContent = "Opening technical support form…";

  try {
    window.BPC_OPEN_TECHNICAL_ZOHO_FORM({
      escalationReason: escalation?.reason || "",
      latestQuestion: getLatestQuestion(instance),
      latestAnswer: getLatestAnswer(instance),
      transcript: getTranscript(instance),
      snapshot,
      context,
      customer,
      transcriptUrl,
      transcriptRef
    });

    console.log("Technical Bernie support form opened with:", {
      transcriptUrl,
      transcriptRef
    });

    status.classList.remove("is-error");
    status.textContent = "Technical support form opened below with transcript link.";
  } catch (err) {
    console.error("Technical Bernie form open failed:", err);

    status.classList.add("is-error");
    status.textContent =
      "Transcript link was created, but the support form failed to open: " +
      (err?.message || "Unknown error");

    openBtn.disabled = false;
  }
});
}

  function customerWantsSupportTicket(message) {
    const text = String(message || "").toLowerCase().trim();

    const yesSignals = [
      "yes",
      "yeah",
      "yep",
      "please",
      "ok",
      "okay",
      "send it",
      "submit it",
      "open ticket",
      "open a ticket",
      "raise ticket",
      "raise a ticket",
      "support ticket",
      "send to support",
      "send to technical",
      "send to technical manager",
      "technical manager",
      "contact support",
      "request support",
      "request technical support",
      "technical support request",
      "send support request",
      "pass it on",
      "forward it"
    ];

    return yesSignals.some(function (signal) {
      return text === signal || text.includes(signal);
    });
  }

  async function askBernie(instance, promptText, options) {
    const input = qs(instance, ".bpc-bernie-input");
    const askBtn = qs(instance, ".bpc-bernie-ask-btn");
    const endpoint = instance.dataset.bernieEndpoint || "/apps/commercial-ai";
    const assistant = instance.dataset.bernieAssistant || "bernie";

    const message = String(promptText || input?.value || "").trim();

    if (
      instance.__lastSupportEscalation &&
      instance.__lastSupportEscalation.shouldOffer &&
      customerWantsSupportTicket(message)
    ) {
      if (input) input.value = "";

      addMessage(instance, "user", message);

      instance.__bernieHistory = instance.__bernieHistory || [];
      instance.__bernieHistory.push({
        role: "user",
        content: message
      });

      addMessage(
        instance,
        "bot",
        "Of course — I’ll open the BPC support form and prefill it with what you’ve already told me."
      );

      addSupportEscalationPanel(instance, instance.__lastSupportEscalation);

      setTimeout(function () {
        const panel = qs(instance, ".bpc-bernie-support-panel");
        const openBtn = qs(panel || instance, ".bpc-bernie-support-send");
        openBtn?.click();
      }, 100);

      return;
    }

    if (!instance.__bernieHistory) {
      instance.__bernieHistory = [];
    }

    if (input) input.value = "";

    let attachments = [];

    try {
      // consume:true is important: files are sent once, then cleared so follow-up prompts do not resend them.
      attachments = await getPageAttachments(instance, { consume: true });
    } catch (err) {
      console.error("Bernie attachment error:", err);

      addMessage(
        instance,
        "bot",
        err && err.message ? err.message : "Could not read the uploaded file."
      );

      return;
    }

    if (!message && !attachments.length) return;

    const finalMessage =
      message ||
      "Please review the uploaded file(s) and help me with this support request.";

    const attachedFileText = attachments.length
      ? "\n\nAttached file(s): " + attachments.map(function (item) { return item.name; }).join(", ")
      : "";

    const visibleMessage =
      String(options?.displayText || finalMessage || "").trim() + attachedFileText;

    addMessage(instance, "user", visibleMessage);

    instance.__bernieHistory.push({
      role: "user",
      content: finalMessage
    });

    try {
      if (askBtn) {
        askBtn.disabled = true;
        askBtn.setAttribute("aria-busy", "true");
      }

      addTyping(instance);

      const context = getPageContext(instance);
      const snapshot = getPageSnapshot(instance);

      const selectedProduct =
        snapshot?.selectedProduct ||
        context?.selectedProduct ||
        null;

      const payload = {
        assistant,
        message: finalMessage,
        history: instance.__bernieHistory.slice(-8),
        snapshot,
        context,
        selectedProduct
      };

      if (attachments.length) {
        payload.attachments = attachments;
        // Keep the original singular field for backward compatibility with older Worker versions.
        payload.attachment = attachments[0];
      }

      const res = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify(payload)
      });

      const data = await res.json().catch(function () {
        return null;
      });

      removeTyping(instance);

      if (!res.ok || !data || data.ok === false) {
        throw new Error((data && data.error) || "Bernie could not answer just now.");
      }

      const answer = data.answer || "Sorry, I could not answer that.";

      addMessage(instance, "bot", answer);

      instance.__bernieHistory.push({
        role: "assistant",
        content: answer
      });

      instance.__bernieHistory = instance.__bernieHistory.slice(-30);

      addFeedbackControls(instance, answer);

      if (data.supportEscalation && data.supportEscalation.shouldOffer) {
        instance.__lastSupportEscalation = data.supportEscalation;

        addMessage(
          instance,
          "bot",
          "This may need checked by the BPC Technical Manager. Would you like me to help you open a support ticket?"
        );

        addSupportEscalationPanel(instance, data.supportEscalation);
      } else {
        // Keep an existing support offer visible after follow-up questions.
        // The customer may still need to open the ticket even if the latest answer did not emit a new escalation marker.
      }
    } catch (err) {
      console.error(err);
      removeTyping(instance);

      addMessage(
        instance,
        "bot",
        err && err.message ? err.message : "Sorry, Bernie could not answer just now."
      );
    } finally {
      if (askBtn) {
        askBtn.disabled = false;
        askBtn.removeAttribute("aria-busy");
      }
    }
  }

  function bindBernieUpload(instance) {
    const wrap = qs(instance, "[data-bernie-upload-wrap]");
    const input = qs(instance, ".bpc-bernie-upload-input");
    const note = qs(instance, "[data-bernie-upload-note]");
    const clearBtn = qs(instance, "[data-bernie-upload-clear]");

    if (!wrap || !input) return;

    const defaultNote = note ? note.textContent : "";

    input.addEventListener("change", function () {
      const files = Array.from(input.files || []).filter(Boolean).slice(0, 5);

      if (!files.length) {
        wrap.classList.remove("has-file");
        if (note) note.textContent = defaultNote;
        if (clearBtn) clearBtn.hidden = true;
        return;
      }

      try {
        files.forEach(validateBernieUploadFile);
      } catch (err) {
        input.value = "";
        wrap.classList.remove("has-file");
        if (note) note.textContent = err && err.message ? err.message : "Please upload JPG, PNG, WebP or PDF files only.";
        if (clearBtn) clearBtn.hidden = true;
        return;
      }

      wrap.classList.add("has-file");
      if (note) {
        note.textContent = files.length === 1
          ? "Attached: " + files[0].name
          : "Attached " + files.length + " files: " + files.map(function (file) { return file.name; }).join(", ");
      }
      if (clearBtn) clearBtn.hidden = false;
    });

    clearBtn?.addEventListener("click", function () {
      input.value = "";
      wrap.classList.remove("has-file");
      if (note) note.textContent = defaultNote;
      clearBtn.hidden = true;
    });
  }

  function bindBernie(instance) {
    if (!instance || instance.dataset.bound === "1") return;

    instance.dataset.bound = "1";
    instance.__bernieHistory = [];

    bindBernieUpload(instance);

    const card = qs(instance, ".bpc-bernie-card");
    const input = qs(instance, ".bpc-bernie-input");
    const askBtn = qs(instance, ".bpc-bernie-ask-btn");
    const minimiseBtn = qs(instance, ".bpc-bernie-minimise");
    const toggleBtn = qs(instance, ".bpc-bernie-toggle-btn");
    const shell = qs(instance, ".bpc-bernie-shell");

    if (askBtn) {
      askBtn.addEventListener("click", function () {
        askBernie(instance);
      });
    }

    if (input) {
      input.addEventListener("keydown", function (event) {
        if (event.key === "Enter") {
          event.preventDefault();
          askBernie(instance);
        }
      });
    }

    qsa(instance, "[data-bernie-prompt]").forEach(function (btn) {
      btn.addEventListener("click", function () {
        askBernie(instance, btn.getAttribute("data-bernie-prompt") || "");
      });
    });

    if (minimiseBtn) {
      minimiseBtn.addEventListener("click", function (event) {
        event.stopPropagation();

        if (!card) return;

        card.classList.toggle("is-minimised");

        minimiseBtn.textContent = card.classList.contains("is-minimised") ? "+" : "−";
        minimiseBtn.setAttribute(
          "aria-label",
          card.classList.contains("is-minimised") ? "Open Bernie" : "Minimise Bernie"
        );
      });
    }

    if (card) {
      card.addEventListener("click", function () {
        if (!card.classList.contains("is-minimised")) return;

        card.classList.remove("is-minimised");

        if (minimiseBtn) {
          minimiseBtn.textContent = "−";
          minimiseBtn.setAttribute("aria-label", "Minimise Bernie");
        }
      });
    }

    if (toggleBtn) {
      toggleBtn.addEventListener("click", function () {
        if (!shell) return;

        const isOpen = shell.classList.toggle("is-open");
        toggleBtn.setAttribute("aria-expanded", isOpen ? "true" : "false");

        if (isOpen && card) {
          card.classList.remove("is-minimised");

          if (minimiseBtn) {
            minimiseBtn.textContent = "−";
            minimiseBtn.setAttribute("aria-label", "Minimise Bernie");
          }

          setTimeout(function () {
            qs(instance, ".bpc-bernie-input")?.focus();
          }, 150);
        }
      });
    }
  }

  function initBernie() {
    document.querySelectorAll(BERNIE_SELECTOR).forEach(bindBernie);
  }

  window.BPC_BERNIE_ASK = function (target, message, options) {
    const instance =
      typeof target === "string"
        ? document.querySelector(target)
        : target;

    if (!instance) return;

    return askBernie(instance, message, options);
  };

  window.BPC_BERNIE_OPEN_SUPPORT_PANEL = function (target, escalation) {
    const instance =
      typeof target === "string"
        ? document.querySelector(target)
        : target;

    if (!instance) return;

    instance.__lastSupportEscalation = {
      shouldOffer: true,
      reason: escalation?.reason || "Customer submitted technical support details.",
      ...escalation
    };

    addSupportEscalationPanel(instance, instance.__lastSupportEscalation);
  };

  document.addEventListener("DOMContentLoaded", initBernie);
  window.BPC_BERNIE_INIT = initBernie;
})();